import { useEffect, useMemo } from 'react';
import * as yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useJpa, useRequest } from '@stores';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import { generateValidation, QUESTION_TYPE } from './ddf.validation';
import Section from './Section';
import classNames from 'classnames';
import FileDownloadList from '@components/file-download-list/FileDownloadList';
import { MarkdownRenderer } from '@src/app-components/markdown-renderer/MarkdownRenderer';

import './ddf.scss';

const getFormHasQuestions = (formData) =>
  formData?.jpaSections?.reduce((acc, section) => acc + section?.jpaQuestions?.length, 0) > 0;

const Form = ({
  formData,
  isInformation,
  onChange = () => {},
  isReadOnly,
  onValidationChange,
  stepNo,
  componentId,
}) => {
  const formHasQuestions = useMemo(() => getFormHasQuestions(formData), [formData]);

  const { defaultValues, jpaQuestionVisibility, setFullQuestionIds } = useJpa();
  const { isSectionTouched, isSectionHidden } = useRequest();
  const isHidden = isSectionHidden(stepNo);

  const initialSectionDocuments = formData?.jpaFormDocuments?.filter((file) => file?.isInitialDisplayLocation);
  const formSectionDocuments = formData?.jpaFormDocuments?.filter((file) => file?.isFullDisplayLocation);

  useEffect(() => {
    if (formData?.jpaSections) {
      const fullQuestionIDs = formData?.jpaSections
        ?.filter((section) => section.displayLocation === 'Full')
        .map((section) => section.jpaQuestions.map((question) => question.jpaQuestionID))
        .flat();
      setFullQuestionIds(fullQuestionIDs);
    }
  }, [formData?.jpaSections, setFullQuestionIds]);

  const validationSchema = useMemo(() => {
    if (isHidden) {
      return yup.object().shape({});
    } else {
      return yup.object().shape(generateValidation(formData?.jpaSections, jpaQuestionVisibility));
    }
  }, [formData?.jpaSections, jpaQuestionVisibility, isHidden]);

  const methods = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const {
    formState: { errors, isValid },
    trigger,
    reset,
    getValues,
  } = methods;

  const isTouched = isSectionTouched(stepNo);

  useEffect(() => {
    onValidationChange?.(isHidden || isValid);
  }, [isHidden, isValid, onValidationChange]);

  useEffect(() => {
    const resetValues = { ...defaultValues };
    const currentValues = getValues();
    const { jpaContactAnswerSet } = useJpa.getState();

    Object.entries(currentValues).forEach(([key, value]) => {
      if (key.startsWith('q-')) resetValues[key] ??= value;
    });

    formData?.jpaSections?.forEach((section) => {
      section.jpaQuestions?.forEach((question) => {
        const questionType = question.jpaQuestionTypeID;
        const inputName = `${questionType}-${question.jpaQuestionID}`;

        switch (questionType) {
          case QUESTION_TYPE.yesNo:
            resetValues[inputName] ??= null;
            break;

          case QUESTION_TYPE.option:
            resetValues[inputName] ??= question.allowsMultiselect ? [] : '';
            break;

          case QUESTION_TYPE.shortAnswer:
          case QUESTION_TYPE.longAnswer:
          case QUESTION_TYPE.number:
          case QUESTION_TYPE.date:
            resetValues[inputName] ??= '';
            break;
          case QUESTION_TYPE.contact:
          case QUESTION_TYPE.basicContact: {
            const contacts = jpaContactAnswerSet.filter((c) => c.questionID === question.jpaQuestionID);
            resetValues[inputName] ??= contacts.length > 0 ? contacts : null;
            break;
          }
          case QUESTION_TYPE.table:
            resetValues[inputName] ??= currentValues[inputName] ?? null;
            break;

          default:
            break;
        }
      });
    });

    reset(resetValues);
    void trigger();
  }, [defaultValues, getValues, formData?.jpaSections, reset, trigger]);

  useEffect(() => {
    void trigger();
  }, [validationSchema, trigger]);

  const containerStyles = classNames('ddf-form-container', {
    'ddf-no-questions': !formHasQuestions,
  });

  return (
    <FormProvider {...methods}>
      <div className={containerStyles}>
        {formHasQuestions && isTouched && <ErrorSummary defaultExpanded={true} errors={errors} />}
        <div className='ddf-form'>
          {formData?.subtitle && <div className='ddf-form-subtitle'>{formData?.subtitle}</div>}
          <h2 className='ddf-form-title'>{formData?.formTitle}</h2>
          <div className='ddf-form-header'>
            <MarkdownRenderer text={isInformation ? formData?.jpaInitialContent : formData?.jpaRegionHeader} />
            <FileDownloadList files={isInformation ? initialSectionDocuments : formSectionDocuments} />
          </div>
          {formData?.jpaSections?.map((sectionData) => (
            <Section
              jpaFormDocuments={formData?.jpaFormDocuments}
              isInformation={isInformation}
              componentId={componentId}
              isReadOnly={isReadOnly}
              key={sectionData?.jpaSectionID}
              onChange={onChange}
              sectionData={sectionData}
              stepNo={stepNo}
            />
          ))}
          <div className='ddf-form-footer'>
            <MarkdownRenderer text={formData?.jpaRegionFooter} />
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default Form;
